/* cart-drawer.js - FULL REPLACEMENT
   - Full cart drawer loading overlay spinner during cart updates
   - Event delegation (bind once, survives drawer HTML replacement)
   - Persists checkbox UI state (Age Gate / Leave Parcel / Order Notes enabled + draft note)
     across drawer refreshes (quantity change, remove, add related, etc.)
   - Avoids duplicate updates (Leave Parcel + Order Notes) when using custom elements
*/

(function () {
  "use strict";

  // Prevent double-init if the script is loaded twice
  if (window.__EL_CART_DRAWER_INIT__) return;
  window.__EL_CART_DRAWER_INIT__ = true;

  // ============================================================
  // Persist drawer checkbox UI state across drawer re-render
  // ============================================================
  const DRAWER_STATE_KEY = "__el_cart_drawer_state_v1";

  function readDrawerState() {
    try {
      const raw = localStorage.getItem(DRAWER_STATE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  function writeDrawerState(patch) {
    try {
      const current = readDrawerState();
      const next = { ...current, ...patch };
      localStorage.setItem(DRAWER_STATE_KEY, JSON.stringify(next));
      return next;
    } catch {
      return patch;
    }
  }

  function getOrderNoteTextarea() {
    // Prefer explicit id, otherwise fallback to your markup
    return (
      document.querySelector("#cart-drawer-order-note") ||
      document.querySelector(".cart-drawer-order-notes-textarea textarea") ||
      document.querySelector('textarea[name="note"]')
    );
  }

  function captureDrawerUiState() {
    const ageGate = document.querySelector("#cart-drawer-age-gate")?.checked;

    const leaveParcel = document.querySelector("#leave-parcel")?.checked;

    const orderNotesEnabled =
      document.querySelector("#cart-drawer-order-note-checkbox")?.checked;

    const noteEl = getOrderNoteTextarea();
    const orderNoteDraft = noteEl ? String(noteEl.value ?? "") : undefined;

    return {
      ageGate: typeof ageGate === "boolean" ? ageGate : undefined,
      leaveParcel: typeof leaveParcel === "boolean" ? leaveParcel : undefined,
      orderNotesEnabled:
        typeof orderNotesEnabled === "boolean" ? orderNotesEnabled : undefined,
      orderNoteDraft: typeof orderNoteDraft === "string" ? orderNoteDraft : undefined,
    };
  }

  function applyDrawerUiState(state) {
    // Age gate (client-only)
    const ageGateEl = document.querySelector("#cart-drawer-age-gate");
    if (ageGateEl && typeof state.ageGate === "boolean") {
      ageGateEl.checked = state.ageGate;
    }

    // Leave Parcel (server attribute, but we also restore UI)
    const leaveParcelEl = document.querySelector("#leave-parcel");
    if (leaveParcelEl && typeof state.leaveParcel === "boolean") {
      leaveParcelEl.checked = state.leaveParcel;
    }

    // Order notes checkbox + textarea visibility
    const notesCheckbox = document.querySelector("#cart-drawer-order-note-checkbox");
    const notesTextarea = getOrderNoteTextarea();

    if (notesCheckbox && typeof state.orderNotesEnabled === "boolean") {
      notesCheckbox.checked = state.orderNotesEnabled;
    }

    if (notesTextarea) {
      const enabled =
        (notesCheckbox && notesCheckbox.checked) ||
        (typeof state.orderNotesEnabled === "boolean" && state.orderNotesEnabled);

      notesTextarea.style.display = enabled ? "block" : "none";

      // Restore draft only if server note is empty (avoid overwriting saved notes)
      if (!notesTextarea.value.trim() && state.orderNoteDraft && state.orderNoteDraft.trim()) {
        notesTextarea.value = state.orderNoteDraft;
      }
    }
  }

  // ============================================================
  // Loading overlay (FULL DRAWER)
  // ============================================================
  let cartDrawerLoadingCount = 0;

  function injectDrawerLoadingCssOnce() {
    if (document.getElementById("cart-drawer-loading-css")) return;

    const style = document.createElement("style");
    style.id = "cart-drawer-loading-css";
    style.textContent = `
      .cart-drawer-box { position: relative; }

      .cart-drawer-loading-overlay{
        position:absolute;
        inset:0;
        display:none;
        align-items:center;
        justify-content:center;
        background:rgba(255,255,255,.65);
        z-index:10;
      }

      /* drawer-level loading state survives innerHTML replacement */
      .cart-drawer.is-loading .cart-drawer-loading-overlay{
        display:flex;
      }

      .cart-drawer-loading-spinner{ width:36px; height:36px; }
      .cart-drawer-loading-spinner::after{
        content:"";
        display:block;
        width:100%; height:100%;
        border:3px solid currentColor;
        border-right-color:transparent;
        border-radius:50%;
        animation:cartDrawerSpin .7s linear infinite;
      }
      @keyframes cartDrawerSpin{ to{ transform:rotate(360deg);} }

      /* Keep close button clickable above overlay */
      .cart-drawer-header-right-close{ position:relative; z-index:11; }
    `;
    document.head.appendChild(style);
  }

  function ensureDrawerOverlayMarkup() {
    injectDrawerLoadingCssOnce();

    const box = document.querySelector(".cart-drawer-box");
    if (!box) return;

    if (!box.querySelector(".cart-drawer-loading-overlay")) {
      const overlay = document.createElement("div");
      overlay.className = "cart-drawer-loading-overlay";
      overlay.setAttribute("aria-hidden", "true");
      overlay.innerHTML =
        '<span class="cart-drawer-loading-spinner" aria-hidden="true"></span>';
      box.appendChild(overlay);
    }
  }

  function setCartDrawerLoading(isLoading) {
    const drawer = document.querySelector(".cart-drawer");
    if (!drawer) return;

    if (isLoading) cartDrawerLoadingCount++;
    else cartDrawerLoadingCount = Math.max(0, cartDrawerLoadingCount - 1);

    const on = cartDrawerLoadingCount > 0;
    drawer.classList.toggle("is-loading", on);
    drawer.setAttribute("aria-busy", on ? "true" : "false");

    // Ensure overlay exists even after drawer refresh
    if (on) ensureDrawerOverlayMarkup();
  }

  async function withCartDrawerLoading(fn) {
    setCartDrawerLoading(true);
    try {
      return await fn();
    } finally {
      setCartDrawerLoading(false);
    }
  }

  // ============================================================
  // Drawer open/close
  // ============================================================
  function openCartDrawer() {
    const drawer = document.querySelector(".cart-drawer");
    if (!drawer) return;
    drawer.classList.add("cart-drawer--active");

    // Apply persisted UI state on open
    applyDrawerUiState(readDrawerState());

    if (window.jQuery) window.jQuery("html").addClass("lock");
    else document.documentElement.classList.add("lock");
  }

  function closeCartDrawer() {
    const drawer = document.querySelector(".cart-drawer");
    if (!drawer) return;
    drawer.classList.remove("cart-drawer--active");

    if (window.jQuery) window.jQuery("html").removeClass("lock");
    else document.documentElement.classList.remove("lock");
  }

  // Expose globally (theme uses these)
  window.openCartDrawer = openCartDrawer;
  window.closeCartDrawer = closeCartDrawer;

  // ============================================================
  // Cart count
  // ============================================================
  function updateCartItemCounts(count) {
    document.querySelectorAll(".cart-count").forEach((el) => {
      el.textContent = count;
    });
  }

  window.updateCartItemCounts = updateCartItemCounts;

  // ============================================================
  // Drawer refresh (section render)
  // ============================================================
  async function updateCartDrawer() {
    // Capture current UI state BEFORE DOM replacement
    const uiBefore = captureDrawerUiState();
    writeDrawerState(uiBefore);

    const res = await fetch("/?section_id=cart-drawer", { credentials: "same-origin" });
    if (!res.ok) throw new Error(`Failed to load cart drawer section: ${res.status}`);

    const text = await res.text();
    const html = document.createElement("div");
    html.innerHTML = text;

    const newInner = html.querySelector(".cart-drawer")?.innerHTML;
    if (!newInner) throw new Error("Cart drawer HTML missing .cart-drawer");

    const drawer = document.querySelector(".cart-drawer");
    if (!drawer) throw new Error("Current page missing .cart-drawer");

    const wasLoading = drawer.classList.contains("is-loading");

    drawer.innerHTML = newInner;

    if (wasLoading) drawer.classList.add("is-loading");

    // Recreate overlay + re-init slider (new DOM)
    ensureDrawerOverlayMarkup();
    initRelatedProductsSlider();

    // Apply stored UI state AFTER replacement (uiBefore wins)
    const stored = readDrawerState();
    applyDrawerUiState({ ...stored, ...uiBefore });

    // Re-bind once-only handlers if needed
    addCartDrawerListeners();
  }

  window.updateCartDrawer = updateCartDrawer;

  // ============================================================
  // Helpers
  // ============================================================
  function debounce(func, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  }

  async function postJson(url, payload) {
    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`${url} failed: ${res.status}`);
    return res.json();
  }

  async function getCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) throw new Error(`/cart.js failed: ${res.status}`);
    return res.json();
  }

  // ============================================================
  // Leave Parcel attribute fallback (ONLY if not using <l-cart-attribute>)
  // ============================================================
  async function updateCartAttributesFallback() {
    const leaveParcelCheckbox = document.querySelector("#leave-parcel");
    if (!leaveParcelCheckbox) return;

    const leaveParcel = leaveParcelCheckbox.checked ? "Authorized" : "";

    // Persist UI state immediately
    writeDrawerState({ leaveParcel: leaveParcelCheckbox.checked });

    await withCartDrawerLoading(async () => {
      await postJson("/cart/update.js", {
        attributes: { "Leave Parcel": leaveParcel },
      });
      await updateCartDrawer();
      const cart = await getCart();
      updateCartItemCounts(cart.item_count);
    });
  }

  window.updateCartAttributes = updateCartAttributesFallback;

  // ============================================================
  // Related products slider init (safe)
  // ============================================================
  function initRelatedProductsSlider() {
    if (!window.jQuery || !window.jQuery.fn || !window.jQuery.fn.slick) return;

    const $ = window.jQuery;
    const $slider = $(".cart-drawer .related-products__slider");
    if (!$slider.length) return;

    $slider.each(function () {
      const $el = $(this);
      if ($el.hasClass("slick-initialized")) return;

      $el.slick({
        dots: true,
        arrows: false,
        infinite: true,
        slidesToShow: 1,
        slidesToScroll: 1,
      });
    });
  }

  // ============================================================
  // Notes saving fallback (ONLY if not using <l-cart-note>)
  // ============================================================
  function updateOrderNotesFallback() {
    const orderNoteField = getOrderNoteTextarea();
    if (!orderNoteField) return;

    const orderNote = String(orderNoteField.value || "").trim();

    // Store draft (also covers the "checkbox stays checked" UX)
    writeDrawerState({ orderNoteDraft: orderNote });

    fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: orderNote }),
    })
      .then((r) => r.json())
      .catch((e) => console.error("Error updating Order Notes:", e));
  }

  const debouncedUpdateOrderNotesFallback = debounce(updateOrderNotesFallback, 500);

  // ============================================================
  // Bind once + event delegation
  // ============================================================
  let listenersBound = false;

  function addCartDrawerListeners() {
    if (listenersBound) return;
    listenersBound = true;

    ensureDrawerOverlayMarkup();
    initRelatedProductsSlider();

    // Apply persisted state on first bind
    applyDrawerUiState(readDrawerState());

    // Click events (open/close/qty/remove/related/checkout gate)
    document.addEventListener("click", function (e) {
      const drawer = document.querySelector(".cart-drawer");
      if (!drawer) return;

      // OPEN triggers
      const openTrigger = e.target.closest('[aria-controls="CartDrawer"], a[href="/cart"]');
      if (openTrigger) {
        e.preventDefault();
        openCartDrawer();
        return;
      }

      // Close button
      if (e.target.closest(".cart-drawer-header-right-close")) {
        e.preventDefault();
        closeCartDrawer();
        return;
      }

      // Backdrop click closes (ignore clicks inside the box)
      if (e.target.closest(".cart-drawer") && !e.target.closest(".cart-drawer-box")) {
        closeCartDrawer();
        return;
      }

      // Quantity +/-
      const qtyBtn = e.target.closest(".cart-drawer-quantity-selector button");
      if (qtyBtn) {
        e.preventDefault();

        // Ignore clicks while loading
        if (drawer.classList.contains("is-loading")) return;

        const item = qtyBtn.closest(".cart-drawer-item");
        const key = item?.getAttribute("data-line-item-key");
        if (!key) return;

        const input = item.querySelector(".cart-drawer-quantity-selector input");
        const currentQty = Number(input?.value || 0);

        const isUp = qtyBtn.classList.contains("cart-drawer-quantity-selector-plus");
        let newQty = isUp ? currentQty + 1 : currentQty - 1;
        if (newQty < 0) newQty = 0;

        // Save current state before update
        writeDrawerState(captureDrawerUiState());

        withCartDrawerLoading(async () => {
          const cart = await postJson("/cart/update.js", {
            updates: { [key]: newQty },
          });
          updateCartItemCounts(cart.item_count);
          await updateCartDrawer();
        }).catch(console.error);

        return;
      }

      // Remove
      const removeBtn = e.target.closest(".cart-drawer-remove");
      if (removeBtn) {
        e.preventDefault();

        if (drawer.classList.contains("is-loading")) return;

        const item = removeBtn.closest(".cart-drawer-item");
        const key = item?.getAttribute("data-line-item-key");
        if (!key) return;

        // Save current state before update
        writeDrawerState(captureDrawerUiState());

        withCartDrawerLoading(async () => {
          const cart = await postJson("/cart/update.js", {
            updates: { [key]: 0 },
          });
          updateCartItemCounts(cart.item_count);
          await updateCartDrawer();
        }).catch(console.error);

        return;
      }

      // Related products add
      const relatedAtc = e.target.closest(".cart-drawer__related-products .btn-atc");
      if (relatedAtc) {
        e.preventDefault();

        if (drawer.classList.contains("is-loading")) return;

        const id = relatedAtc.getAttribute("data-id") || relatedAtc.dataset?.id;
        if (!id) return;

        // Save current state before update
        writeDrawerState(captureDrawerUiState());

        withCartDrawerLoading(async () => {
          // /cart/add.js does NOT return item_count reliably -> fetch /cart.js after
          await fetch("/cart/add.js", {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: Number(id), quantity: 1 }),
          });

          await updateCartDrawer();
          const cart = await getCart();
          updateCartItemCounts(cart.item_count);
        }).catch(console.error);

        return;
      }

      // Checkout age gate
      const btn = e.target.closest('.cart-drawer-footer [name="checkout"]');
      if (btn) {
        const gate =
          document.querySelector("#age-gate") || document.querySelector("#cart-drawer-age-gate");

        if (gate && !gate.checked) {
          e.preventDefault();
          alert("You must confirm you are over 18 years of age");
        }
      }
    });

    // Change events (checkboxes)
    document.addEventListener("change", function (e) {
      // Age gate checkbox persist (client-only)
      if (e.target && e.target.id === "cart-drawer-age-gate") {
        writeDrawerState({ ageGate: e.target.checked });
        return;
      }

      // Leave parcel
      if (e.target && e.target.id === "leave-parcel") {
        // persist UI state
        writeDrawerState({ leaveParcel: e.target.checked });

        // IMPORTANT:
        // If you are using <l-cart-attribute>, let it handle the API call.
        // Otherwise fallback to updateCartAttributesFallback().
        if (!e.target.closest("l-cart-attribute")) {
          updateCartAttributesFallback().catch(console.error);
        }
        return;
      }

      // Notes checkbox
      if (e.target && e.target.id === "cart-drawer-order-note-checkbox") {
        writeDrawerState({ orderNotesEnabled: e.target.checked });

        const textarea = getOrderNoteTextarea();
        if (!textarea) return;

        textarea.style.display = e.target.checked ? "block" : "none";

        // Clear note only if user explicitly unticks
        if (!e.target.checked) {
          textarea.value = "";
          writeDrawerState({ orderNoteDraft: "" });

          // If you're using <l-cart-note>, it will post updates itself on input.
          if (!textarea.closest("l-cart-note")) {
            debouncedUpdateOrderNotesFallback();
          } else {
            textarea.dispatchEvent(new Event("input", { bubbles: true }));
          }
        }
        return;
      }
    });

    // Notes typing (debounced) + draft persist
    document.addEventListener("input", function (e) {
      const textarea = e.target && (e.target.id === "cart-drawer-order-note" || e.target.matches(".cart-drawer-order-notes-textarea textarea") || e.target.matches('textarea[name="note"]'))
        ? e.target
        : null;

      if (!textarea) return;

      writeDrawerState({ orderNoteDraft: textarea.value });

      // Avoid duplicate posting if <l-cart-note> is present
      if (!textarea.closest("l-cart-note")) {
        debouncedUpdateOrderNotesFallback();
      }
    });

    // AJAX product add-to-cart forms (outside drawer)
    document.querySelectorAll('form[action="/cart/add"]').forEach((form) => {
      if (form.__drawerBound) return;
      form.__drawerBound = true;

      form.addEventListener("submit", async (ev) => {
        ev.preventDefault();

        // Save current state before update
        writeDrawerState(captureDrawerUiState());

        await withCartDrawerLoading(async () => {
          await fetch("/cart/add.js", {
            method: "POST",
            credentials: "same-origin",
            body: new FormData(form),
            headers: { Accept: "application/json" },
          });

          await updateCartDrawer();
          const cart = await getCart();
          updateCartItemCounts(cart.item_count);
          openCartDrawer();
        });
      });
    });
  }

  // ============================================================
  // CART COMPONENTS (your custom elements) - safe define
  // ============================================================
  function l_debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function l_fetchConfig(type = "json") {
    return {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: `application/${type}` },
    };
  }

  // CART NOTE
  if (!customElements.get("l-cart-note")) {
    class LTACartNote extends HTMLElement {
      connectedCallback() {
        this.textAreaEl = this.querySelector("textarea");
        if (!this.textAreaEl) return;

        // Ensure we have an id so other code can find it
        if (!this.textAreaEl.id) this.textAreaEl.id = "cart-drawer-order-note";

        // Ensure initial visibility matches checkbox state + stored state
        const state = readDrawerState();
        applyDrawerUiState(state);

        this.textAreaEl.addEventListener(
          "input",
          l_debounce(() => this.handleInput(), 400)
        );
      }

      handleInput() {
        const cartNote = `${this.textAreaEl.value}`.trim();

        // Persist draft in local storage
        writeDrawerState({ orderNoteDraft: cartNote });

        const url =
          (window.theme && theme.routes && theme.routes.cart_update_url) || "/cart/update.js";

        fetch(url, {
          ...l_fetchConfig(),
          body: JSON.stringify({ note: cartNote }),
        })
          .then((r) => r.json())
          .then(() => {
            document.querySelectorAll("l-cart-note").forEach((el) => {
              if (el !== this && el.textAreaEl) el.textAreaEl.value = cartNote;
            });
          })
          .catch(console.error);
      }
    }

    customElements.define("l-cart-note", LTACartNote);
  }

  // CART ATTRIBUTE
  if (!customElements.get("l-cart-attribute")) {
    class LTACartAttribute extends HTMLElement {
      connectedCallback() {
        this.inputEl = this.querySelector("input");
        if (!this.inputEl) return;

        this.inputEl.addEventListener("input", this.handleInput.bind(this));
      }

      async handleInput() {
        const url =
          (window.theme && theme.routes && theme.routes.cart_update_url) || "/cart/update.js";

        const checked = !!this.inputEl.checked;
        const cartAttribute = `${checked ? this.dataset.checkedValue : ""}`.trim();

        // Persist UI state (special-case Leave Parcel)
        if (this.inputEl.id === "leave-parcel" || this.dataset.attributeName === "Leave Parcel") {
          writeDrawerState({ leaveParcel: checked });
        }

        await withCartDrawerLoading(async () => {
          const res = await fetch(url, {
            ...l_fetchConfig(),
            body: JSON.stringify({
              attributes: {
                [this.dataset.attributeName]: cartAttribute,
              },
            }),
          });

          const data = await res.json();

          // Keep other instances in sync
          document.querySelectorAll("l-cart-attribute").forEach((el) => {
            if (el !== this && el.inputEl) {
              el.inputEl.checked =
                data.attributes?.[this.dataset.attributeName] === this.dataset.checkedValue;
            }
          });

          // Keep drawer in sync
          await updateCartDrawer();
          const cart = await getCart();
          updateCartItemCounts(cart.item_count);
        }).catch(console.error);
      }
    }

    customElements.define("l-cart-attribute", LTACartAttribute);
  }

  // ============================================================
  // Init
  // ============================================================
  addCartDrawerListeners();

  // Ensure overlay exists on first load and after DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      ensureDrawerOverlayMarkup();
      initRelatedProductsSlider();
      applyDrawerUiState(readDrawerState());
    });
  } else {
    ensureDrawerOverlayMarkup();
    initRelatedProductsSlider();
    applyDrawerUiState(readDrawerState());
  }
})();
